import apiClient from '../client';
import ENDPOINTS from '../endpoints';

/* The lookups and writes only sign-up needs. The profile itself is saved through
   profileService.saveParentProfile, which the Profile page shares. */
const onboardingService = {
  /* Schools nearest a point, each with the grade-level class networks parents
     can join. `distance` comes back in miles. */
  schoolsNear: async ({ lat, lng, page = 1, limit = 20 }) => {
    const res = await apiClient.post(ENDPOINTS.SCHOOL.CLASS_DATA, { lat, lng, page, limit });
    const payload = res?.data ?? res;
    const list = payload?.data || [];
    return {
      schools: (Array.isArray(list) ? list : []).map((s) => ({
        id: String(s._id),
        name: s.name || '',
        photo: s.photo || '',
        distance: typeof s.distance === 'number' ? s.distance : null,
        grades: (s.classes || []).map((c) => ({
          networkId: c.networkId,
          name: c.title || '',
        })),
      })),
      hasNext: !!payload?.hasNext,
      total: Number(payload?.total ?? 0),
    };
  },

  /* Class groups that already exist under a grade, so a second room parent can
     join one instead of creating a duplicate. The grade network itself comes
     back in the same list — only its sub groups are classes. */
  classesForGrade: async (gradeNetworkId) => {
    const res = await apiClient.get(ENDPOINTS.CLASS_ADMIN.CLASSES_BY_GRADE(gradeNetworkId));
    const { myList = [], otherList = [] } = res?.data || {};
    return [...myList, ...otherList]
      .filter((c) => c.networkType === 'subGroup')
      .map((c) => ({
        networkId: c.networkId,
        title: c.title || '',
        description: c.description || '',
        image: c.image || '',
        isJoined: !!c.isJoined,
        memberStatus: c.memberStatus || null,
        /* "room parent" is the platform's classAdmin flag, not plain membership */
        roomParents: (c.classAdmin || []).filter((a) => a.status).length,
      }));
  },

  /* The room parent's own class hub — a sub group hanging off the grade network
     they picked. Passing userId enrolls them in it as its admin. Created open,
     not private, so the next room parent for the same class can join straight
     in rather than waiting on an approval nobody is watching for. */
  createClassGroup: ({ networkId, name, userId, isPrivate = false }) =>
    apiClient.post(ENDPOINTS.CLASS_ADMIN.CREATE_CLASS, {
      networkId,
      name,
      privacy: isPrivate,
      ...(userId ? { userId } : {}),
    }),

  /* Joins a class group that already exists. A private one answers with a
     `pending` member — that class stays invisible until someone approves it. */
  joinClassGroup: async ({ gradeNetworkId, classNetworkId, userId }) => {
    const res = await apiClient.post(ENDPOINTS.CLASS_ADMIN.JOIN_CLASS, {
      networkId: gradeNetworkId,
      classId: classNetworkId,
      userId,
    });
    return { pending: res?.data?.status === 'pending' };
  },

  /* Marks the user as a room parent of a class group — what /myGroups and the
     "N room parents already" count both read. */
  markRoomParent: ({ classNetworkId, userId }) =>
    apiClient.post(ENDPOINTS.CLASS_ADMIN.SET_CLASS_ADMIN, {
      networkId: classNetworkId,
      userId,
      status: true,
    }),
};

export default onboardingService;
